const API_URL = import.meta.env.VITE_API_URL || '';

export interface ClientProfile {
    id?: string;
    slug: string;
    display_name: string;
    markdown: string;
    is_active?: boolean;
    updated_at?: string;
}

function authHeaders(token?: string): HeadersInit {
    return {
        'Content-Type': 'application/json',
        ...(token ? { 'X-Admin-Token': token } : {}),
    };
}

export async function getPublicProfile(slug: string): Promise<ClientProfile | null> {
    try {
        const response = await fetch(`${API_URL}/profiles/${slug}`);
        if (response.status === 404) return null;
        if (!response.ok) throw new Error(`Profile fetch failed: ${response.status}`);
        return await response.json();
    } catch (error) {
        console.warn('[ProfilesAPI] Could not load client profile:', error);
        return null;
    }
}

export async function listProfiles(token?: string): Promise<ClientProfile[]> {
    const response = await fetch(`${API_URL}/admin/profiles`, {
        headers: authHeaders(token),
    });
    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Profile list failed: ${response.status}`);
    }
    return await response.json();
}

export async function saveProfile(profile: ClientProfile, token?: string): Promise<ClientProfile> {
    const response = await fetch(`${API_URL}/admin/profiles`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify(profile),
    });
    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Profile save failed: ${response.status}`);
    }
    return await response.json();
}

export async function setProfileActive(slug: string, isActive: boolean, token?: string): Promise<ClientProfile> {
    const response = await fetch(`${API_URL}/admin/profiles/${slug}`, {
        method: 'PATCH',
        headers: authHeaders(token),
        body: JSON.stringify({ is_active: isActive }),
    });
    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Profile update failed: ${response.status}`);
    }
    return await response.json();
}

/**
 * Pull the client's city out of the profile markdown, e.g. a line like
 * "Headquarters: Denver, CO", "**Location:** Birmingham, Alabama", or "Based in Austin, TX".
 * Returns null when the profile doesn't state one.
 */
export function getProfileCity(profile: ClientProfile): string | null {
    const match = profile.markdown.match(
        /^[\s>*_#-]*(?:headquarters|headquartered in|hq|location|city|based in|home office)\b[\s*_]*[:\-–—]?[\s*_]*(.+)$/im
    );
    if (!match) return null;
    const city = match[1].replace(/[*_`]/g, '').replace(/[.;]+\s*$/, '').trim();
    return city && city.length <= 80 ? city : null;
}
